import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  MessageCircle,
  FileCheck,
  Factory,
  Wrench,
  CheckCircle2,
  Key,
} from "lucide-react";
import SectionHeading from "../common/SectionHeading";

gsap.registerPlugin(ScrollTrigger);

const processSteps = [
  {
    number: "01",
    title: "Design Consultation",
    icon: MessageCircle,
    steps: [
      "A call with Design Mentor",
      "Requirement Gathering Form",
      "Conceptual discussions",
      "Technical Audit (Site Measurements)",
      "Creative Reports – Moodboard; Detailed Quote Reports with a 'Nominal Professional Service Fee'",
    ],
  },
  {
    number: "02",
    title: "Design Signoff",
    icon: FileCheck,
    steps: [
      "Structured Design Phase",
      "3D – Visualizations",
      "Design Document – 3D views + Material Specs",
      "Agreement & Design Sign off",
      "Additional advantage of Furniture suggestions",
    ],
  },
  {
    number: "03",
    title: "Production",
    icon: Factory,
    steps: [
      "Material Procurement",
      "Parallel works in Site",
      "Production in Factory",
      "Quality check in before dispatch",
      "Dispatch of Cleaned and Packed material",
    ],
  },
  {
    number: "04",
    title: "Installation",
    icon: Wrench,
    steps: [
      "Installation by Expert Carpenters",
      "Civil works",
      "Plumbing works",
      "Hardware and Light installation",
      "Painting Deep Cleaning",
    ],
  },
  {
    number: "05",
    title: "Quality",
    icon: CheckCircle2,
    steps: [
      "Finishes Integrity",
      "Multi-point Inspection",
      "Quality Inspection Report",
      "Quality Sign off",
    ],
  },
  {
    number: "06",
    title: "Handover",
    icon: Key,
    steps: [
      "Warranty Document Handover",
      "Project Walkthrough",
      "Keys Handover",
    ],
  },
];

export default function ProcessTeaser() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;

    if (!section || !track) return;

    // Only enable horizontal scroll on desktop
    const isDesktop = window.innerWidth >= 1024;

    if (!isDesktop) {
      // On mobile, just allow natural horizontal scroll
      return;
    }

    let ctx = null;
    let timeoutId = null;
    let rAF1 = null;
    let rAF2 = null;

    // Wait for layout to be calculated
    const initAnimation = () => {
      // Check if elements still exist
      if (!sectionRef.current || !trackRef.current) return;

      const totalWidth = track.scrollWidth - section.offsetWidth;

      if (totalWidth <= 0) return;

      // Clean up any existing context first
      if (ctx) {
        try {
          ctx.revert();
        } catch (e) {
          // Ignore errors
        }
        ctx = null;
      }

      try {
        ctx = gsap.context(() => {
          gsap.to(track, {
            x: -totalWidth,
            ease: "none",
            scrollTrigger: {
              trigger: section,
              start: "top top",
              end: () => `+=${totalWidth}`,
              pin: true,
              scrub: 1,
              anticipatePin: 1,
              invalidateOnRefresh: true,
              pinSpacing: true,
              markers: false, // Set to true for debugging
            },
          });
        }, section);
      } catch (error) {
        console.warn("GSAP animation setup error:", error);
      }
    };

    // Wait for images and layout to be ready
    timeoutId = setTimeout(() => {
      rAF1 = requestAnimationFrame(() => {
        rAF2 = requestAnimationFrame(initAnimation);
      });
    }, 100);

    // Handle resize
    const handleResize = () => {
      if (ctx) {
        try {
          ctx.revert();
          ctx = null;
        } catch (e) {
          // Ignore errors
        }
      }
      ScrollTrigger.refresh();
      if (window.innerWidth >= 1024) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(initAnimation);
          });
        }, 100);
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });

    // Cleanup function
    return () => {
      // Cancel any pending timeouts and animation frames
      if (timeoutId) clearTimeout(timeoutId);
      if (rAF1) cancelAnimationFrame(rAF1);
      if (rAF2) cancelAnimationFrame(rAF2);
      window.removeEventListener("resize", handleResize);

      if (ctx) {
        try {
          // Kill ALL ScrollTriggers immediately to unpin any pinned elements
          // This must be synchronous to prevent React from trying to remove moved nodes
          const allTriggers = ScrollTrigger.getAll();
          allTriggers.forEach((trigger) => {
            try {
              trigger.kill();
            } catch (e) {
              // Ignore individual trigger kill errors
            }
          });
          ctx.revert();
        } catch (e) {
          // Silently ignore cleanup errors - React may have already unmounted
          console.warn("GSAP cleanup error (safe to ignore):", e);
        }
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-background-card overflow-hidden"
    >
      <div className="min-h-screen flex flex-col justify-center py-20">
        <div className="container-custom section-padding mb-12">
          <SectionHeading
            label="Our Process"
            title="Design, Production & Execution Journey"
            align="left"
          />
          <p className="text-text-secondary text-lg mt-4 max-w-3xl">
            Throughout this journey you will receive <span className="font-semibold text-brand-primary">week wise progress report</span>.
          </p>
        </div>

        <div
          ref={trackRef}
          className="flex gap-8 pl-8 lg:pl-24 overflow-x-auto lg:overflow-x-visible scrollbar-hide"
        >
          {processSteps.map((step, index) => (
            <div
              key={step.number}
              className="flex-none w-[85vw] sm:w-[60vw] lg:w-[40vw] xl:w-[32vw]"
            >
              <div className="h-full bg-white rounded-3xl p-8 lg:p-10 shadow-soft hover:shadow-medium transition-all duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-brand-primary rounded-2xl flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-brand-primary tracking-wider">
                      PHASE {step.number}
                    </span>
                    <h3 className="font-heading text-2xl text-brand-primary mt-1">
                      {step.title}
                    </h3>
                  </div>
                </div>

                <div className="space-y-3">
                  {step.steps.map((stepItem, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-accent/10 flex items-center justify-center mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-brand-accent"></div>
                      </div>
                      <p className="text-text-secondary text-sm leading-relaxed flex-1">
                        {stepItem}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Progress indicator */}
                <div className="mt-8 flex items-center gap-2">
                  {processSteps.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all ${
                        i <= index
                          ? "bg-brand-primary w-8"
                          : "bg-border-light w-4"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* CTA Card */}
          <div className="flex-none w-[85vw] sm:w-[60vw] lg:w-[40vw] xl:w-[32vw]">
            <div className="h-full bg-gradient-brand rounded-3xl p-8 lg:p-10 flex flex-col justify-center text-white mr-8 lg:mr-24">
              <h3 className="font-heading text-3xl mb-4 text-white">
                Ready to transform your space?
              </h3>
              <p className="text-white/80 mb-8">
                See our complete process and understand how we bring your vision
                to life, step by step.
              </p>
              <Link
                to="/process"
                className="inline-flex items-center gap-2 text-white font-medium hover:gap-3 transition-all"
              >
                Explore our process
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
